import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeviceImage } from "@/components/DeviceFrames";
import api from "@/lib/api";
import { PORTFOLIO_CATEGORIES } from "@/data/content";

export function categoryLabel(value) {
  const found = PORTFOLIO_CATEGORIES.find((c) => c.value === value);
  return found ? found.label : value;
}

export function ProjectCard({ item, onOpen }) {
  return (
    <div
      data-testid={`project-card-${item.id}`}
      className="rounded-2xl bg-[#0f172a] border border-slate-800 overflow-hidden hover:border-[#1d4ed8] transition-colors group"
    >
      <div className="aspect-[4/3] relative overflow-hidden bg-[#050b18]">
        <img
          src={item.image_url}
          alt={`${item.title} — ${categoryLabel(item.category)}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        />
        {item.is_demo && (
          <span className="absolute top-3 left-3 text-[10px] font-mono-x uppercase tracking-wider px-2 py-1 rounded bg-black/70 text-[#38bdf8] border border-[#38bdf8]/30">
            Concept de démonstration
          </span>
        )}
      </div>
      <div className="p-6">
        <span className="text-[10px] font-mono-x uppercase tracking-widest text-[#38bdf8]">{categoryLabel(item.category)}</span>
        <h3 className="font-bold text-white text-base mt-1.5 mb-1.5">{item.title}</h3>
        <p className="text-xs text-slate-400 leading-relaxed mb-4">{item.description}</p>
        <button
          type="button"
          onClick={() => onOpen(item)}
          data-testid={`project-view-${item.id}`}
          className="inline-flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
        >
          Voir le projet <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function ProjectDialog({ item, open, onClose }) {
  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="project-dialog"
        className="bg-[#0f172a] border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-mono-x uppercase tracking-widest text-[#38bdf8]">{categoryLabel(item.category)}</span>
            {item.is_demo && (
              <Badge variant="outline" className="border-[#38bdf8]/40 text-[#38bdf8] text-[10px] font-mono-x uppercase">
                Concept de démonstration
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl font-bold text-white">{item.title}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{item.description}</DialogDescription>
        </DialogHeader>

        <DeviceImage
          device={item.device}
          src={item.image_url}
          alt={`${item.title} — aperçu du concept`}
          className={item.device === "phone" ? "mx-auto max-w-[240px]" : ""}
        />

        {item.long_description && (
          <p className="text-sm text-slate-300 leading-relaxed">{item.long_description}</p>
        )}

        {item.features && item.features.length > 0 && (
          <div>
            <h4 className="text-xs font-mono-x uppercase tracking-widest text-[#38bdf8] mb-2">Fonctionnalités clés</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {item.features.map((f) => (
                <li key={f} className="text-xs text-slate-300 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Link
          to="/contact"
          data-testid="project-dialog-cta"
          className="mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#1d4ed8] hover:bg-blue-600 text-white text-xs font-mono-x font-bold uppercase tracking-wider transition-colors"
        >
          Je veux un projet comme celui-ci <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </DialogContent>
    </Dialog>
  );
}

export function PortfolioGrid({ limit, category, showFilters = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/portfolio")
      .then((res) => {
        if (mounted) setItems(res.data);
      })
      .catch(() => {
        if (mounted) setError("Impossible de charger les réalisations pour le moment.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (category) list = list.filter((i) => i.category === category);
    if (filter !== "all") list = list.filter((i) => i.category === filter);
    if (limit) list = list.slice(0, limit);
    return list;
  }, [items, category, filter, limit]);

  if (loading) {
    return (
      <div data-testid="portfolio-loading" className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((n) => (
          <div key={n} className="rounded-2xl bg-[#0f172a] border border-slate-800 h-72 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p data-testid="portfolio-error" className="text-sm text-slate-400 text-center py-10">
        {error}
      </p>
    );
  }

  return (
    <div>
      {showFilters && (
        <div data-testid="portfolio-filters" className="flex flex-wrap gap-2 mb-10">
          {PORTFOLIO_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              data-testid={`filter-${c.value}`}
              onClick={() => setFilter(c.value)}
              className={`px-4 py-2 rounded-lg text-xs font-mono-x uppercase tracking-wider border transition-colors ${
                filter === c.value
                  ? "bg-[#1d4ed8] border-[#1d4ed8] text-white"
                  : "bg-[#0f172a] border-slate-800 text-slate-400 hover:border-[#38bdf8]/50 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <p data-testid="portfolio-empty" className="text-sm text-slate-400 text-center py-10">
          Aucun projet dans cette catégorie pour le moment.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item) => (
            <ProjectCard key={item.id} item={item} onOpen={setSelected} />
          ))}
        </div>
      )}

      <ProjectDialog item={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
